import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFab from "@/components/WhatsAppFab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JournalDetail() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setNotFound(false);
        (async () => {
            try {
                const { data } = await axios.get(`${API}/articles/${slug}`);
                if (alive) setArticle(data);
            } catch {
                if (alive) setNotFound(true);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [slug]);

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso">
            <SiteNav />
            <main>
                {loading ? (
                    <div className="pt-40 pb-24 text-center text-cafe-mocha">Loading…</div>
                ) : notFound || !article ? (
                    <div className="max-w-3xl mx-auto px-6 md:px-12 pt-40 pb-24 text-center">
                        <div className="font-serif text-5xl text-cafe-espresso">Not found</div>
                        <Link to="/journal" className="mt-8 inline-flex items-center gap-2 text-cafe-olive hover:text-cafe-oliveDark">
                            <ArrowLeft className="h-4 w-4" /> Back to journal
                        </Link>
                    </div>
                ) : (
                    <article data-testid="article-detail">
                        <section className="pt-28 md:pt-36">
                            <div className="max-w-3xl mx-auto px-6 md:px-12">
                                <Link to="/journal" className="inline-flex items-center gap-2 text-cafe-mocha hover:text-cafe-olive text-sm mb-8">
                                    <ArrowLeft className="h-4 w-4" /> Journal
                                </Link>
                                {article.tags && article.tags.length > 0 && (
                                    <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                                        {article.tags.join(" · ")}
                                    </p>
                                )}
                                <h1 className="font-serif text-4xl md:text-6xl lg:text-[72px] text-cafe-espresso leading-[0.95] text-balance" style={{ fontWeight: 500 }}>
                                    {article.title}
                                </h1>
                                <div className="mt-5 text-sm text-cafe-espresso/60">
                                    {article.author} · {new Date(article.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                                </div>
                            </div>
                        </section>
                        <section className="mt-10 md:mt-14">
                            <div className="max-w-5xl mx-auto px-6 md:px-12">
                                <div className="overflow-hidden aspect-[16/9] bg-cafe-olive">
                                    {article.cover_image ? (
                                        <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-cafe-cream/70 font-display italic">
                                            Cover image
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>
                        <section className="py-16 md:py-24">
                            <div className="max-w-3xl mx-auto px-6 md:px-12">
                                <div className="font-serif text-xl md:text-2xl text-cafe-espresso/85 leading-relaxed whitespace-pre-wrap">
                                    {article.content}
                                </div>
                            </div>
                        </section>
                    </article>
                )}
            </main>
            <SiteFooter />
            <WhatsAppFab />
        </div>
    );
}
